import React from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import MainLayout from "../Layouts/main";
import Icon from "../components/Icon";
import InternalLink from "../components/InternalLink";
import { NewIntegrationModal } from "./NewIntegrationModal";
import IntegrationAlerts from "./IntegrationAlerts";
import { WhatsAppEmbeddedModal } from "./WhatsAppEmbeddedModal";
import { WhatsAppTemplateModal } from "./WhatsAppTemplateModal";
import { FacebookAdsEmbeddedModal } from "./FacebookAdsEmbeddedModal";
import CommunityIntegrations from "./CommunityIntegrations";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import CreateCommunityIntegrationModal from "./CreateCommunityIntegrationModal";
import { toast } from "sonner";
import { useConfirm } from "../hooks/useConfirm";
import { useAuth } from "../context/AuthContext";
import Message from "../components/Message";
import GoogleDrivePicker from "../components/GoogleDrivePicker";
import { getAllPlans } from "@utils";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@shadcn-in-nx/ui/tooltip";

export const INTEGRATION_TYPES = [
    {
        id: "facebook",
        name: "Facebook Ads",
        icon: "Facebook",
        inDevelopment: false,
        imageIcon: "/icons/facebook.png",
        helpText:
            "Connect to Facebook Ads to manage ad accounts, campaigns, insights, audiences, and page engagement",
        apiUrl: "https://graph.facebook.com",
        category: "Marketing",
        embeddedSetup: true,
        capabilities: [
            "Ad Account Management: Read account details, list users, view activity logs",
            "Campaign Management: Create, read, update, and delete ad campaigns",
            "Ad Set Management: Create and manage ad sets with targeting, budgets, and schedules",
            "Ad Management: Create and manage individual ads and ad creatives",
            "Insights & Reporting: Get performance metrics (impressions, clicks, spend, CTR, CPC) at account, campaign, ad set, or ad level with breakdowns by age, gender, country, device",
            "Custom Audiences: Create and manage custom audiences, add/remove users with hashed PII",
            "Page Engagement: Read page posts, comments, likes, shares, and engagement metrics",
        ],
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Facebook Integration",
                placeholder: "My Facebook Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Access Token (from Facebook login)",
                required: true,
                readOnly: true,
                helpText:
                    "This will be auto-filled after completing the Facebook login",
            },
        },
    },
    {
        id: "whatsapp",
        name: "WhatsApp Business",
        icon: "MessageSquare",
        imageIcon: "/icons/whatsapp.png",
        helpText:
            "Connect to WhatsApp Business to send messages to your customers",
        apiUrl: "https://graph.facebook.com",
        category: "Communication",
        embeddedSetup: true,
        configFields: {
            name: {
                type: "text",
                defaultValue: "My WhatsApp Business Integration",
                placeholder: "My WhatsApp Business Integration",
                required: false,
            },
            phoneNumberId: {
                type: "text",
                placeholder: "Phone Number ID (from embedded signup)",
                required: true,
                readOnly: true,
                helpText:
                    "This will be auto-filled after completing the embedded signup",
            },
            wabaId: {
                type: "text",
                placeholder: "WhatsApp Business Account ID",
                required: true,
                readOnly: true,
                helpText:
                    "This will be auto-filled after completing the embedded signup",
            },
            businessId: {
                type: "text",
                placeholder: "Business Portfolio ID",
                required: true,
                readOnly: true,
                helpText:
                    "This will be auto-filled after completing the embedded signup",
            },
            accessToken: {
                type: "password",
                placeholder: "Business Access Token",
                required: true,
                readOnly: true,
                helpText:
                    "This will be auto-filled after completing the embedded signup",
            },
        },
    },
    {
        id: "sql",

        name: "SQL Database",
        icon: "Database",
        inDevelopment: false,
        imageIcon: "/icons/sql.png",
        helpText:
            "Connect to SQL databases (MySQL, PostgreSQL, SQLite, SQL Server) to execute queries on your external database.",
        category: "Database",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My SQL Database",
                placeholder: "My SQL Database",
                required: false,
            },
            databaseType: {
                type: "select",
                defaultValue: "mysql",
                required: true,
                helpText: "Type of SQL database",
                options: [
                    { value: "mysql", label: "MySQL" },
                    { value: "postgresql", label: "PostgreSQL" },
                    // { value: "sqlite", label: "SQLite" },
                    { value: "mariadb", label: "MariaDB" },
                    { value: "mssql", label: "Microsoft SQL Server" },
                    { value: "bigquery", label: "Google BigQuery" },
                ],
            },
            host: {
                type: "text",
                placeholder:
                    "Enter database host (e.g., localhost, 192.168.1.100)",
                required: true,
                helpText: "Database server hostname or IP address",
                dependsOn: {
                    field: "databaseType",
                    values: ["mysql", "postgresql", "mariadb", "mssql"],
                },
            },
            port: {
                type: "number",
                placeholder: "Enter database port",
                required: false,
                helpText:
                    "Database server port number (MySQL: 3306, PostgreSQL: 5432, SQL Server: 1433)",
                dependsOn: {
                    field: "databaseType",
                    values: ["mysql", "postgresql", "mariadb", "mssql"],
                },
            },
            username: {
                type: "text",
                placeholder: "Enter database username",
                required: true,
                helpText: "Database username",
                dependsOn: {
                    field: "databaseType",
                    values: ["mysql", "postgresql", "mariadb", "mssql"],
                },
            },
            password: {
                type: "password",
                placeholder: "Enter database password",
                required: true,
                helpText: "Database password",
                dependsOn: {
                    field: "databaseType",
                    values: ["mysql", "postgresql", "mariadb", "mssql"],
                },
            },
            database: {
                type: "text",
                placeholder: "Enter database name",
                required: true,
                helpText: "Name of the database to connect to",
                dependsOn: {
                    field: "databaseType",
                    values: ["mysql", "postgresql", "mariadb", "mssql"],
                },
            },
            schema: {
                type: "text",
                placeholder:
                    "Enter schema name (default: public for PostgreSQL, dbo for SQL Server)",
                defaultValue: "public",
                required: false,
                helpText: "PostgreSQL/SQL Server schema name",
                dependsOn: {
                    field: "databaseType",
                    values: ["postgresql", "mssql"],
                },
            },
            filename: {
                type: "text",
                placeholder: "Enter SQLite database file path",
                required: true,
                helpText: "Path to SQLite database file",
                dependsOn: { field: "databaseType", values: ["sqlite"] },
            },
            ssl: {
                type: "checkbox",
                defaultValue: false,
                helpText: "Enable SSL connection",
                dependsOn: {
                    field: "databaseType",
                    values: ["mysql", "postgresql", "mariadb", "mssql"],
                },
            },
            connectTimeout: {
                type: "number",
                placeholder:
                    "Connection timeout in milliseconds (default: 10000)",
                defaultValue: 10000,
                required: false,
                helpText: "Connection timeout in milliseconds",
                dependsOn: {
                    field: "databaseType",
                    values: [
                        "mysql",
                        "postgresql",
                        "mariadb",
                        "mssql",
                        "sqlite",
                    ],
                },
            },
            projectId: {
                type: "text",
                placeholder: "Enter your Google Cloud project ID",
                required: true,
                helpText: "Your Google Cloud project ID",
                dependsOn: { field: "databaseType", values: ["bigquery"] },
            },
            credentials: {
                type: "password",
                placeholder: "Paste the entire JSON file contents here",
                required: true,
                helpText:
                    "Paste the ENTIRE contents of your service account JSON key file (including { and }). Download it from Google Cloud Console > IAM > Service Accounts > Keys > Add Key > JSON",
                dependsOn: { field: "databaseType", values: ["bigquery"] },
            },
            dataset: {
                type: "text",
                placeholder: "Default dataset (optional)",
                required: false,
                helpText: "Default dataset to use for queries",
                dependsOn: { field: "databaseType", values: ["bigquery"] },
            },
            location: {
                type: "select",
                defaultValue: "US",
                required: false,
                helpText: "BigQuery data location",
                options: [
                    { value: "US", label: "United States (US)" },
                    { value: "EU", label: "European Union (EU)" },
                    { value: "us-central1", label: "Iowa (us-central1)" },
                    { value: "us-east1", label: "South Carolina (us-east1)" },
                    { value: "us-west1", label: "Oregon (us-west1)" },
                    { value: "europe-west1", label: "Belgium (europe-west1)" },
                    { value: "europe-west2", label: "London (europe-west2)" },
                    { value: "asia-east1", label: "Taiwan (asia-east1)" },
                    {
                        value: "asia-northeast1",
                        label: "Tokyo (asia-northeast1)",
                    },
                    {
                        value: "asia-southeast1",
                        label: "Singapore (asia-southeast1)",
                    },
                    {
                        value: "australia-southeast1",
                        label: "Sydney (australia-southeast1)",
                    },
                ],
                dependsOn: { field: "databaseType", values: ["bigquery"] },
            },
        },
    },
    {
        id: "awsS3",
        name: "AWS S3",
        icon: "AWS",
        imageIcon: "/icons/aws-s3.png",
        helpText: "Connect to AWS S3 to manage files",
        apiUrl: "https://aws.amazon.com/s3",
        category: "Cloud Storage",
        backendConfig: {
            useOAuth: false,
            method: "getAWSSTSAuthUrl",
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My AWS S3 Integration",
                placeholder: "My AWS S3 Integration",
                required: false,
            },
            accessKeyId: {
                type: "password",
                placeholder: "Enter your AWS access key ID",
                required: true,
            },
            secretAccessKey: {
                type: "password",
                placeholder: "Enter your AWS secret access key",
                required: true,
            },
            region: {
                type: "text",
                placeholder: "Enter your AWS region",
                required: true,
            },
            bucketName: {
                type: "text",
                placeholder: "Enter your AWS bucket name",
                required: true,
            },
            cdn: {
                type: "text",
                placeholder: "Enter your CDN URL",
                required: false,
            },
            userConfigured: {
                type: "hidden",
                defaultValue: true,
                required: false,
            },
        },
    },
    {
        id: "openai",
        name: "OpenAI",

        imageIcon: "/icons/openai.png",
        helpText: "Connect to OpenAI to use GPT models",
        apiUrl: "https://api.openai.com/v1",
        category: "AI",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My OpenAI Integration",
                placeholder: "My OpenAI Integration",
                required: false,
            },
            apiKey: {
                type: "password",
                placeholder: "Enter your OpenAI API key",
                required: true,
                helpText:
                    "Get your API key from https://platform.openai.com/api-keys",
            },
            baseUrl: {
                type: "url",
                value: "https://api.openai.com/v1",
                readOnly: true,
            },
        },
    },
    {
        id: "anthropic",
        name: "Anthropic",
        imageIcon: "/icons/anthropic.png",
        helpText: "Connect to Anthropic to use Claude models",
        apiUrl: "https://api.anthropic.com",
        category: "AI",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Anthropic Integration",
                placeholder: "My Anthropic Integration",
                required: false,
            },
            apiKey: {
                type: "password",
                placeholder: "Enter your Anthropic API key",
                required: true,
                helpText:
                    "Get your API key from https://console.anthropic.com/",
            },
            baseUrl: {
                type: "url",
                value: "https://api.anthropic.com",
                readOnly: true,
            },
        },
    },
    {
        id: "perplexity",
        name: "Perplexity",
        imageIcon: "/icons/perplexity.png",
        helpText:
            "Connect to Perplexity to use AI models with real-time search",
        apiUrl: "https://api.perplexity.ai",
        category: "AI",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Perplexity Integration",
                placeholder: "My Perplexity Integration",
                required: false,
            },
            apiKey: {
                type: "password",
                placeholder: "Enter your Perplexity API key",
                required: true,
                helpText:
                    "Get your API key from https://www.perplexity.ai/account/api/keys",
            },
            baseUrl: {
                type: "url",
                value: "https://api.perplexity.ai",
                readOnly: true,
            },
        },
    },
    // {
    //     id: "whatsapp",
    //     name: "WhatsApp Business",
    //     icon: "WhatsApp",
    //     inDevelopment: true,
    //     imageIcon: "/icons/whatsapp.png",
    //     helpText: "Connect to WhatsApp Business API to send messages",
    //     apiUrl: "https://graph.facebook.com",
    //     configFields: {
    //         name: {
    //             type: "text",
    //             defaultValue: "My WhatsApp Integration",
    //             placeholder: "My WhatsApp Integration",
    //             required: false,
    //         },
    //         access_token: {
    //             type: "password",
    //             placeholder: "Enter your System User Access Token",
    //             required: true,
    //             helpText:
    //                 "The system user access token of your WhatsApp business account",
    //         },
    //         businessAccountId: {
    //             type: "text",
    //             placeholder: "Enter your Business Account ID",
    //             required: true,
    //             helpText:
    //                 "The business account ID of your WhatsApp business account",
    //         },
    //     },
    // },
    {
        id: "google",
        name: "Google Drive",
        icon: "Google",
        inDevelopment: false,
        imageIcon: "/icons/google-drive.png",
        helpText: "Connect to Google Drive to manage files",
        apiUrl: "https://www.googleapis.com/drive/v3",
        category: "Cloud Storage",
        backendConfig: {
            useOAuth: true,
            method: "getGoogleAuthUrl",
            scopes: [
                // "https://www.googleapis.com/auth/spreadsheets",
                // "https://www.googleapis.com/auth/documents",
                "https://www.googleapis.com/auth/drive.file",
                // "https://www.googleapis.com/auth/drive",
            ],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Google Drive Integration",
                placeholder: "My Google Drive Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Enter your Google Drive access token",
                required: true,
                helpText:
                    "Generate an access token from the Google Cloud Console",
            },
            refreshToken: {
                type: "password",
                placeholder: "Enter your Google Drive refresh token",
                required: true,
                helpText:
                    "Generate a refresh token from the Google Cloud Console",
            },
            baseUrl: {
                type: "url",
                value: "https://www.googleapis.com/drive/v3",
                readOnly: true,
            },
        },
    },
    {
        id: "gmail",
        name: "Gmail",
        icon: "Google",
        inDevelopment: false,
        imageIcon: "/icons/gmail.png",
        helpText: "Connect to Gmail to manage emails",
        apiUrl: "https://www.googleapis.com/drive/v3",
        category: "Communication",
        backendConfig: {
            useOAuth: true,
            method: "getGoogleAuthUrl",
            scopes: [
                "https://www.googleapis.com/auth/gmail.readonly",
                "https://www.googleapis.com/auth/gmail.send",
                "https://www.googleapis.com/auth/gmail.labels",
                "https://www.googleapis.com/auth/gmail.compose",
                "https://www.googleapis.com/auth/gmail.modify",
            ],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Gmail Integration",
                placeholder: "My Gmail Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Enter your Gmail access token",
                required: true,
                helpText:
                    "Generate an access token from the Google Cloud Console",
            },
            refreshToken: {
                type: "password",
                placeholder: "Enter your Gmail refresh token",
                required: true,
                helpText:
                    "Generate a refresh token from the Google Cloud Console",
            },
            baseUrl: {
                type: "url",
                value: "https://www.googleapis.com/drive/v3",
                readOnly: true,
            },
        },
    },
    {
        id: "google-calendar",
        name: "Google Calendar",
        platform: "google-calendar",
        icon: "Calendar",
        inDevelopment: false,
        imageIcon: "/icons/google-calendar.png",
        helpText: "Connect to Google Calendar to manage events and schedules",
        apiUrl: "https://www.googleapis.com/calendar/v3",
        category: "Productivity",
        backendConfig: {
            useOAuth: true,
            method: "getGoogleCalendarAuthUrl",
            scopes: ["https://www.googleapis.com/auth/calendar"],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Google Calendar Integration",
                placeholder: "My Google Calendar Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Enter your Google Calendar access token",
                required: true,
                helpText:
                    "Generate an access token from the Google Cloud Console",
            },
            refreshToken: {
                type: "password",
                placeholder: "Enter your Google Calendar refresh token",
                required: true,
                helpText:
                    "Generate a refresh token from the Google Cloud Console",
            },
            baseUrl: {
                type: "url",
                value: "https://www.googleapis.com/calendar/v3",
                readOnly: true,
            },
        },
    },
    {
        id: "dropbox",
        name: "Dropbox",
        icon: "Dropbox",
        imageIcon: "/icons/dropbox.png",
        helpText: "Connect to Dropbox to sync and manage your files",
        apiUrl: "https://api.dropbox.com/2",
        category: "Cloud Storage",
        backendConfig: {
            useOAuth: true,
            method: "getDropboxAuthUrl",
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Dropbox Integration",
                placeholder: "My Dropbox Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Enter your Dropbox access token",
                required: true,
                helpText:
                    "Generate an access token from the Dropbox App Console",
            },
            baseUrl: {
                type: "url",
                value: "https://api.dropboxapi.com/2",
                readOnly: true,
            },
        },
    },

    // {
    //     id: "klaviyo",
    //     name: "Klaviyo",
    //     icon: "Klaviyo",
    //     helpText: "Connect to Klaviyo's API for email marketing automation",
    //     apiUrl: "https://a.klaviyo.com/api",
    //     configFields: {
    //         name: {
    //             type: "text",
    //             defaultValue: "My Klaviyo Integration",
    //             placeholder: "My Klaviyo Integration",
    //             required: false,
    //         },
    //         baseUrl: {
    //             type: "url",
    //             value: "https://a.klaviyo.com/api",
    //             readOnly: true,
    //         },
    //         apiKey: {
    //             type: "password",
    //             placeholder: "Enter your Klaviyo API key",
    //             required: true,
    //         },
    //     },
    // },
    {
        id: "awsBedrock",
        name: "AWS Bedrock",
        icon: "AWS",
        imageIcon: "/icons/bedrock.png",
        helpText: "Connect to AWS Bedrock to use AI models",
        apiUrl: "https://bedrock.us-east-1.amazonaws.com",
        category: "AI",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My AWS Bedrock Integration",
                placeholder: "My AWS Bedrock Integration",
                required: false,
            },
            accessKeyId: {
                type: "password",
                placeholder: "Enter your AWS access key ID",
                required: true,
            },
            secretAccessKey: {
                type: "password",
                placeholder: "Enter your AWS secret access key",
                required: true,
            },
            region: {
                type: "text",
                placeholder: "Enter your AWS region",
                required: true,
            },
            model: {
                type: "text",
                placeholder: "Enter the default model you want to use",
                required: true,
            },
        },
    },
    {
        id: "gemini",
        name: "Gemini",
        icon: "Gemini",
        imageIcon: "/icons/gemini.png",
        helpText: "Connect to Google to use AI models",
        category: "AI",
        configFields: {
            name: {
                type: "text",
                placeholder: "My Gemini Integration",
                defaultValue: "My Gemini Integration",
                required: false,
            },
            apiKey: {
                type: "password",
                placeholder: "Enter your Google API key",
                required: true,
            },
            model: {
                type: "text",
                placeholder: "Enter the default model you want to use",
                required: false,
                optional: true,
            },
        },
    },
    {
        id: "smtp",
        name: "SMTP Email",
        icon: "Mail",
        imageIcon: "/icons/smtp.png",
        helpText: "Connect to SMTP server to send emails",
        category: "Communication",
        configFields: {
            name: {
                type: "text",
                placeholder: "My SMTP Integration",
                defaultValue: "My SMTP Integration",
                required: false,
            },
            host: {
                type: "text",
                placeholder: "Enter SMTP host (e.g., smtp.gmail.com)",
                required: true,
                helpText: "SMTP server hostname or IP address",
            },
            port: {
                type: "number",
                placeholder: "Enter SMTP port (e.g., 587, 465, 25)",
                defaultValue: 587,
                required: true,
                helpText: "SMTP server port number",
            },
            secure: {
                type: "text",
                defaultValue: "true",
                helpText:
                    "Use SSL/TLS encryption (true for port 465, false for port 587/25)",
            },
            username: {
                type: "text",
                placeholder: "Enter SMTP username",
                required: true,
                helpText: "SMTP authentication username (usually your email)",
            },
            password: {
                type: "password",
                placeholder: "Enter SMTP password",
                required: true,
                helpText: "SMTP authentication password or app password",
            },
            fromEmail: {
                type: "email",
                placeholder: "Enter default sender email",
                required: true,
                helpText: "Default email address to use as sender",
            },
            fromName: {
                type: "text",
                placeholder: "Enter default sender name",
                required: false,
                helpText: "Default name to display as sender",
            },
        },
    },
    {
        id: "shopify",
        name: "Shopify",
        icon: "Shopify",
        inDevelopment: false,
        imageIcon: "/icons/shopify.png",
        helpText: "Connect to your Shopify store to manage products and orders",
        apiUrl: "https://{shop}.myshopify.com/admin/api",
        category: "E-commerce",
        capabilities: [
            "Product Management: Retrieve products with advanced filtering, pagination, and inventory status tracking",
            "Product Creation: Create new products with variants, images, pricing, inventory, tags, and collections",
            "Customer Management: Access customer information including emails, phone numbers, and order history",
            "Order Management: Retrieve and filter orders with customer details, line items, and fulfillment status",
            "Draft Orders: Create shopping carts and draft orders for customers with custom line items and pricing",
            "Shop Information: Access store owner details and shop configuration",
            "Metafields: Create custom metafields for products, variants, customers, blogs, collections, orders, and more",
            "Metafield Definitions: Create reusable metafield templates that ensure consistency across your store",
            "Metaobject Definitions: Create reusable schema templates for metaobjects (custom data models with structured fields)",
            "Metaobjects: Create and manage custom data model instances with structured data and validation rules",
            "Inventory Tracking: Read and update stock levels across all locations and filter products by inventory status",
            "Locations: Read fulfillment locations (warehouse, FBA, retail, third-party) to sync per-location inventory",
            "Fulfillment Orchestration: Sync fulfillment orders across third-party fulfillment services (Amazon FBA, Walmart, 3PL warehouses) and merchant-managed locations",
            "Discounts & Price Rules: Create and manage discount codes, automatic discounts, and price rules",
            "Returns & Refunds: Read and process customer returns and refund workflows",
            "Gift Cards: Read and modify gift card balances and codes",
            "Reports & Analytics: Query store performance via ShopifyQL",
            "Content: Read articles, blogs, pages, and storefront redirects",
            "Marketing Events: Read and track marketing campaign data",
            "Shipping: Read and configure carrier services and shipping zones",
            "Markets: Read multi-region market configuration",
        ],
        backendConfig: {
            useOAuth: true,
            method: "getShopifyAuthUrl",
            scopes: [
                "read_products",
                "write_products",
                "read_customers",
                "write_customers",
                "read_all_orders",
                "read_orders",
                "read_draft_orders",
                "write_draft_orders",
                "read_returns",
                "write_returns",
                "read_fulfillments",
                "write_fulfillments",
                "read_assigned_fulfillment_orders",
                "write_assigned_fulfillment_orders",
                "read_merchant_managed_fulfillment_orders",
                "write_merchant_managed_fulfillment_orders",
                "read_third_party_fulfillment_orders",
                "write_third_party_fulfillment_orders",
                "read_inventory",
                "write_inventory",
                "read_locations",
                "read_discounts",
                "write_discounts",
                "read_price_rules",
                "write_price_rules",
                "read_gift_cards",
                "write_gift_cards",
                "read_shipping",
                "write_shipping",
                "read_markets",
                "read_content",
                "read_marketing_events",
                "write_marketing_events",
                "read_reports",
                "read_legal_policies",
                "read_metaobjects",
                "write_metaobjects",
                "read_metaobject_definitions",
            ],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Shopify Integration",
                placeholder: "My Shopify Integration",
                required: false,
            },
            shop: {
                type: "text",
                placeholder: "Enter your Shopify shop name (e.g. my-store)",
                required: true,
                helpText:
                    "Your Shopify shop name without the .myshopify.com domain",
            },
        },
    },

    {
        id: "browser",
        name: "Browser Use",
        icon: "Chrome",
        helpText: "Use a browser to interact with websites",
        category: "Automation",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Browser Use Integration",
                placeholder: "My Browser Use Integration",
                required: false,
            },

            baseUrl: {
                type: "url",
                placeholder: "Enter the target website URL",
                required: true,
            },
            username: {
                type: "text",
                placeholder: "Enter website username (if required)",
                required: false,
            },
            password: {
                type: "password",
                placeholder: "Enter website password (if required)",
                required: false,
            },
        },
    },

    {
        id: "microsoft-graph",
        name: "Microsoft 365",
        icon: "Grid",
        inDevelopment: false,
        imageIcon: "/icons/microsoft-graph.png",
        subIcons: [
            { src: "/icons/excel.png", label: "Excel" },
            { src: "/icons/onedrive.png", label: "OneDrive" },
            { src: "/icons/sharepoint.png", label: "SharePoint" },
            { src: "/icons/outlook.png", label: "Outlook" },
            { src: "/icons/outlook-calendar.png", label: "Calendar" },
            { src: "/icons/teams.png", label: "Teams" },
            { src: "/icons/word.png", label: "Word" },
        ],
        helpText:
            "Full Microsoft 365 integration — Excel, OneDrive, SharePoint, Outlook, Calendar, Teams, and Contacts through the Microsoft Graph API",
        apiUrl: "https://graph.microsoft.com/v1.0",
        category: "Productivity",
        backendConfig: {
            useOAuth: true,
            method: "getMicrosoftAuthUrl",
            baseScopes: [
                "https://graph.microsoft.com/User.Read",
                "offline_access",
            ],
            scopeGroups: [
                {
                    id: "files",
                    label: "OneDrive & SharePoint",
                    description:
                        "Read and write files, folders, and SharePoint sites",
                    icon: "/icons/onedrive.png",
                    default: true,
                    scopes: [
                        "https://graph.microsoft.com/Files.Read.All",
                        "https://graph.microsoft.com/Files.ReadWrite.All",
                        "https://graph.microsoft.com/Sites.Read.All",
                    ],
                },
                {
                    id: "excel",
                    label: "Excel",
                    description:
                        "Read and write Excel workbooks, worksheets, tables, and ranges",
                    icon: "/icons/excel.png",
                    default: true,
                    note: "Requires OneDrive & SharePoint",
                    requires: ["files"],
                    scopes: [],
                },
                {
                    id: "mail",
                    label: "Outlook Mail",
                    description: "Read, write, and send emails",
                    icon: "/icons/outlook.png",
                    default: true,
                    scopes: [
                        "https://graph.microsoft.com/Mail.Read",
                        "https://graph.microsoft.com/Mail.ReadWrite",
                        "https://graph.microsoft.com/Mail.Send",
                    ],
                },
                {
                    id: "calendar",
                    label: "Calendar",
                    description:
                        "Read and manage calendar events and scheduling",
                    icon: "/icons/outlook-calendar.png",
                    default: true,
                    scopes: [
                        "https://graph.microsoft.com/Calendars.Read",
                        "https://graph.microsoft.com/Calendars.ReadWrite",
                    ],
                },
                {
                    id: "contacts",
                    label: "Contacts",
                    description: "Read and manage contacts",
                    icon: "/icons/word.png",
                    default: false,
                    scopes: [
                        "https://graph.microsoft.com/Contacts.Read",
                        "https://graph.microsoft.com/Contacts.ReadWrite",
                    ],
                },
                {
                    id: "teams",
                    label: "Teams",
                    description: "List teams, channels, and read team info",
                    icon: "/icons/teams.png",
                    default: false,
                    scopes: [
                        "https://graph.microsoft.com/Team.ReadBasic.All",
                        "https://graph.microsoft.com/Channel.ReadBasic.All",
                    ],
                },
                {
                    id: "teams-messages",
                    label: "Teams Messages",
                    description: "Read and send channel messages",
                    icon: "/icons/teams.png",
                    default: false,
                    adminConsent: true,
                    requires: ["teams"],
                    scopes: [
                        "https://graph.microsoft.com/ChannelMessage.Read.All",
                        "https://graph.microsoft.com/ChannelMessage.Send",
                    ],
                },
                {
                    id: "chat",
                    label: "Teams Chat",
                    description: "Read and send 1:1 and group chat messages",
                    icon: "/icons/teams.png",
                    default: false,
                    scopes: [
                        "https://graph.microsoft.com/Chat.ReadWrite",
                        "https://graph.microsoft.com/ChatMessage.Send",
                    ],
                },
            ],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Microsoft 365 Integration",
                placeholder: "My Microsoft 365 Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Auto-filled after OAuth",
                required: true,
                readOnly: true,
            },
            refreshToken: {
                type: "password",
                placeholder: "Auto-filled after OAuth",
                required: true,
                readOnly: true,
            },
            baseUrl: {
                type: "url",
                value: "https://graph.microsoft.com/v1.0",
                readOnly: true,
            },
        },
    },
    {
        id: "rocketReach",
        name: "RocketReach",
        icon: "RocketReach",
        inDevelopment: false,
        imageIcon: "/icons/rocketreach.png",
        helpText: "Connect to RocketReach to find email addresses",
        category: "Data Enrichment",
        configFields: {
            name: {
                type: "text",
                defaultValue: "RocketReach",
                placeholder: "My RocketReach Integration",
                required: false,
            },
            apiKey: {
                type: "password",
                placeholder: "Enter your RocketReach API key",
                required: true,
            },
            baseUrl: {
                type: "url",
                value: "http://api.rocketreach.co/api/v2",
                readOnly: true,
            },
            headers: {
                type: "array",
                isHidden: true,
                defaultValue: [
                    {
                        key: "Api-Key",
                        value: "${apiKey}",
                    },
                ],
            },
        },
    },
    {
        id: "apify",
        name: "Apify",
        icon: "Puzzle",
        imageIcon: "/icons/apify.png",
        helpText: "Connect to Apify to run actors and fetch datasets.",
        apiUrl: "https://api.apify.com/",
        category: "Automation",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Apify Integration",
                placeholder: "My Apify Integration",
                required: false,
            },

            token: {
                type: "password",
                placeholder: "Enter your Apify API token",
                required: true,
            },
        },
    },
    {
        id: "hoymiles",
        name: "Hoymiles",
        icon: "Hoymiles",
        inDevelopment: false,
        imageIcon: "/icons/hoymiles.png",
        helpText: "Connect to Hoymiles to fetch data from your solar panels.",
        apiUrl: "https://api.hoymiles.com/",
        category: "Energy",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Hoymiles Integration",
                placeholder: "My Hoymiles Integration",
                required: false,
            },
            username: {
                type: "text",
                placeholder: "Enter your Hoymiles username",
                required: true,
            },
            password: {
                type: "password",
                placeholder: "Enter your Hoymiles password",
                required: true,
            },
        },
    },
    {
        id: "shipsgo",
        name: "ShipsGo",
        icon: "Ship",
        inDevelopment: false,
        imageIcon: "/icons/shipsgo.png",
        helpText:
            "Connect to ShipsGo to track your ocean shipments and containers in real-time.",
        apiUrl: "https://api.shipsgo.com/",
        category: "Logistics",
        configFields: {
            name: {
                type: "text",
                defaultValue: "My ShipsGo Integration",
                placeholder: "My ShipsGo Integration",
                required: false,
            },
            apiKey: {
                type: "password",
                placeholder: "Enter your ShipsGo API key",
                required: true,
            },
        },
    },

    {
        id: "enphase-partner",
        name: "Enphase Partner (Recommended)",
        icon: "Enphase",
        inDevelopment: true,
        imageIcon: "/icons/enphase.png",
        helpText: "Connect to Enphase to fetch data from your solar panels.",
        category: "Energy",
        alert: [
            "To use this integration, you need to be a partner with Enphase. You can sign up for an installer plan at https://developer-v4.enphase.com/installer-plans",
        ],
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Enphase Integration",
                placeholder: "My Enphase Integration",
                required: false,
            },
            username: {
                type: "text",
                placeholder: "Enter your Enphase username",
                required: true,
            },
            password: {
                type: "password",
                placeholder: "Enter your Enphase password",
                required: true,
            },
            apiKey: {
                type: "password",
                placeholder: "Enter your Enphase App API key",
                required: true,
            },
            clientId: {
                type: "text",
                placeholder: "Enter your Enphase App client ID",
                required: true,
            },
            clientSecret: {
                type: "password",
                placeholder: "Enter your Enphase App client secret",
                required: true,
            },
        },
    },
    {
        id: "enphase",
        name: "Enphase",
        icon: "Enphase",
        inDevelopment: true,
        imageIcon: "/icons/enphase.png",
        helpText: "Connect to Enphase to fetch data from your solar panels.",
        category: "Energy",
        alert: [
            "This integration requires you to refresh the autnetication every month. You will need to refresh the integration every month to keep it working.",
        ],
        backendConfig: {
            useOAuth: true,
            method: "getEnphaseAuthUrl",
            scopes: ["read"],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Enphase Integration",
                placeholder: "My Enphase Integration",
                required: false,
            },
        },
    },
    {
        id: "odoo",
        name: "Odoo",
        icon: "Database",
        inDevelopment: false,
        imageIcon: "/icons/odoo.png",
        helpText:
            "Connect to Odoo ERP to manage customers, sales, inventory, and more",
        apiUrl: "https://your-odoo-instance.com",
        category: "ERP",
        capabilities: [
            "Contacts & Customers: Manage partners, customers, and suppliers",
            "Sales & Invoicing: Quotations, sales orders, invoices, and payments",
            "Inventory & Warehouse: Stock levels, transfers, and warehouse operations",
            "CRM: Leads, opportunities, and pipeline management",
            "Purchasing: Purchase orders and vendor management",
            "Projects & HR: Tasks, timesheets, employees, and departments",
            "Manufacturing: Production orders and bill of materials",
            "Reporting: Sales analytics, inventory reports, and financial summaries",
            "Works with any installed Odoo module automatically",
        ],
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Odoo Integration",
                placeholder: "My Odoo Integration",
                required: false,
            },
            url: {
                type: "url",
                placeholder:
                    "Enter your Odoo instance URL (e.g., https://demo.odoo.com)",
                required: true,
                helpText: "Base URL of your Odoo instance without /xmlrpc",
            },
            database: {
                type: "text",
                placeholder: "Enter your Odoo database name",
                required: true,
                helpText: "The name of your Odoo database",
            },
            username: {
                type: "text",
                placeholder: "Enter your Odoo username or email",
                required: true,
                helpText: "Your Odoo login username or email",
            },
            password: {
                type: "password",
                placeholder: "Enter your Odoo password",
                required: true,
                helpText: "Your Odoo account password",
            },
        },
    },
    {
        id: "switchsoft",
        name: "Switch Soft",
        icon: "Database",
        inDevelopment: false,
        imageIcon: "/icons/switch-soft.png",
        helpText:
            "Connect to Switch Soft retail management system to access inventory, sales, customers, and reports",
        apiUrl: "https://your-instance.switch-soft.com/",
        category: "ERP",
        capabilities: [
            "Branch Management: Get list of all branches (sucursales)",
            "Product Management: Search and retrieve articles/products with filtering",
            "Inventory Tracking: Get stock levels for products across all branches",
            "Pricing Management: Access pricing information for products",
            "Customer Management: Search and retrieve customer information",
            "Sales Reports: Get sales by branch, article, or date range",
            "Period Reports: Generate comprehensive sales reports for date ranges",
            "Detailed Sales Data: Access complete invoice and line item information",
        ],
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Switch Soft Integration",
                placeholder: "My Switch Soft Integration",
                required: false,
            },
            baseUrl: {
                type: "url",

                placeholder:
                    "Enter your Switch Soft API URL (e.g., https://demo.switch-soft.com/)",
                required: true,
                helpText: "Base URL of your Switch Soft instance",
            },

            token: {
                type: "password",
                placeholder: "Enter your Switch Soft historical data API token",
                required: true,
                helpText: "Your Switch Soft historical data API token",
            },
        },
    },
    {
        id: "customfacebook",
        name: "Facebook Integration with Custom App",
        icon: "Facebook",
        inDevelopment: false,
        imageIcon: "/icons/facebook.png",
        helpText:
            "Connect to the Facebook Graph API to manage ads, pages, insights, audiences, and more via a system user token",
        apiUrl: "https://graph.facebook.com",
        category: "Marketing",
        alert: [
            "Requires you to create a custom app to have access to the full Facebook API",
        ],
        capabilities: [
            "Ad Account Management: Read account details, list users, view activity logs",
            "Campaign Management: Create, read, update, and delete ad campaigns",
            "Ad Set Management: Create and manage ad sets with targeting, budgets, and schedules",
            "Ad Management: Create and manage individual ads and ad creatives",
            "Insights & Reporting: Get performance metrics (impressions, clicks, spend, CTR, CPC) at account, campaign, ad set, or ad level with breakdowns by age, gender, country, device",
            "Custom Audiences: Create and manage custom audiences, add/remove users with hashed PII",
            "Page Management: Read page info, create posts, upload photos/videos, view page insights",
            "Business Manager: View owned ad accounts, pages, pixels, system users, and Instagram accounts",
            "Meta Pixel: Manage pixels and send server-side conversion events via Conversions API",
            "Lead Gen Forms: Retrieve lead form submissions and create test leads",
            "Product Catalogs: Manage product catalogs, products, product sets, and feeds",
        ],
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Custom Facebook Integration",
                placeholder: "My Custom Facebook Integration",
                required: false,
            },
            systemToken: {
                type: "password",
                placeholder: "Enter your Facebook System User Token",
                required: true,
                helpText:
                    "Generate a system user token in Business Manager > Business Settings > Users > System Users > Generate Token. System user tokens do not expire and are recommended for server-to-server API access.",
            },
        },
    },
    {
        id: "outlook",
        name: "Microsoft Outlook",
        icon: "Mail",
        inDevelopment: false,
        imageIcon: "/icons/outlook.png",
        helpText: "Connect to Microsoft Outlook to manage emails and calendar",
        apiUrl: "https://graph.microsoft.com/v1.0",
        category: "Communication",
        backendConfig: {
            useOAuth: true,
            method: "getMicrosoftAuthUrl",
            scopes: [
                "https://graph.microsoft.com/Mail.Read",
                "https://graph.microsoft.com/Mail.Send",
                "https://graph.microsoft.com/Mail.ReadWrite",
                // "https://graph.microsoft.com/Calendars.Read",
                // "https://graph.microsoft.com/Calendars.ReadWrite",
                "https://graph.microsoft.com/User.Read",
            ],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Microsoft Outlook Integration",
                placeholder: "My Microsoft Outlook Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Enter your Microsoft access token",
                required: true,
                helpText:
                    "Generate an access token from the Microsoft Azure Portal",
            },
            refreshToken: {
                type: "password",
                placeholder: "Enter your Microsoft refresh token",
                required: true,
                helpText:
                    "Generate a refresh token from the Microsoft Azure Portal",
            },
            baseUrl: {
                type: "url",
                value: "https://graph.microsoft.com/v1.0",
                readOnly: true,
            },
        },
    },
    {
        id: "onedrive",
        name: "Microsoft OneDrive",
        icon: "Cloud",
        inDevelopment: false,
        imageIcon: "/icons/onedrive.png",
        helpText:
            "Connect to Microsoft OneDrive and SharePoint to read, create, search, and manage files including Excel spreadsheets",
        apiUrl: "https://graph.microsoft.com/v1.0",
        category: "Cloud Storage",
        backendConfig: {
            useOAuth: true,
            method: "getMicrosoftAuthUrl",
            scopes: [
                "https://graph.microsoft.com/Files.Read.All",
                "https://graph.microsoft.com/Files.ReadWrite.All",
                "https://graph.microsoft.com/Sites.Read.All",
                "https://graph.microsoft.com/User.Read",
                "offline_access",
            ],
        },
        configFields: {
            name: {
                type: "text",
                defaultValue: "My Microsoft OneDrive Integration",
                placeholder: "My Microsoft OneDrive Integration",
                required: false,
            },
            accessToken: {
                type: "password",
                placeholder: "Enter your Microsoft access token",
                required: true,
                helpText:
                    "Generate an access token from the Microsoft Azure Portal",
            },
            refreshToken: {
                type: "password",
                placeholder: "Enter your Microsoft refresh token",
                required: true,
                helpText:
                    "Generate a refresh token from the Microsoft Azure Portal",
            },
            baseUrl: {
                type: "url",
                value: "https://graph.microsoft.com/v1.0",
                readOnly: true,
            },
        },
    },
    {
        id: "github",
        name: "GitHub",
        imageIcon: "/icons/github.svg",
        category: "Developer Tools",
        helpText: "GitHub repository sync for your workflow",
        viewOnly: true, // Managed from workflow settings, not connectable here
    },
];

const AvailableIntegrations = INTEGRATION_TYPES.filter(
    (integration) => integration.id !== "browser"
);

export function Integrations() {
    const { t } = useTranslation();
    const {
        user,
        isSuperAdmin,
        isInTrial,
        isFeatureEnabled,
        currentPlanSubscription,
    } = useAuth();
    const location = useLocation();
    const blueprintMissing = location.state?.missingIntegrations || [];
    const blueprintName = location.state?.blueprintName || "";
    const plans = getAllPlans();

    // Get current plan details
    const currentPlanId = currentPlanSubscription?.priceId;
    const currentPlanDetails = plans.find(
        (plan) =>
            plan.priceIdMonthly === currentPlanId ||
            plan.priceIdYearly === currentPlanId ||
            plan.devPriceIdMonthly === currentPlanId ||
            plan.devPriceIdYearly === currentPlanId
    );

    // Check if integrations are enabled for the current plan
    // If user is in trial or has no plan, integrations are disabled
    // If user has a plan, check the enabledFeatures.integrations flag
    const integrationsEnabled =
        isInTrial ||
        (currentPlanDetails &&
            currentPlanDetails.enabledFeatures?.integrations !== false);

    const hasSqlAccess = isFeatureEnabled("sqlIntegration");
    const hasFacebookAdsAccess = isFeatureEnabled("facebookAds");
    const showStarterMessage = !integrationsEnabled;
    const [showNewModal, setShowNewModal] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const [integrationToEdit, setIntegrationToEdit] = React.useState(null);
    const [copySuccess, setCopySuccess] = React.useState(null);
    const [filterText, setFilterText] = React.useState("");
    const [browserIntegration, setBrowserIntegration] = React.useState(null);
    const [selectedIntegrationType, setSelectedIntegrationType] =
        React.useState(null);
    const [deletingIntegration, setDeletingIntegration] = React.useState(null);
    const [showWhatsAppModal, setShowWhatsAppModal] = React.useState(false);
    const [showFacebookAdsModal, setShowFacebookAdsModal] =
        React.useState(false);
    const [showTemplateModal, setShowTemplateModal] = React.useState(false);
    const [selectedWhatsAppIntegration, setSelectedWhatsAppIntegration] =
        React.useState(null);
    const activeTab = location.pathname === "/integrate/community" ? "community" : "connections";
    const setActiveTab = (tab) => nav(tab === "community" ? "/integrate/community" : "/integrate");
    const [autoOpenCreate, setAutoOpenCreate] = React.useState(false);
    const [showCreateCommunityModal, setShowCreateCommunityModal] =
        React.useState(false);
    const nav = useNavigate();

    const { call, error, response, isLoading } = useBluelagoon({
        method: "integrationsList",
    });

    const {
        call: deleteCall,
        error: deleteError,
        response: deleteResponse,
        isLoading: deleteIsLoading,
    } = useBluelagoon({
        method: "deleteIntegration",
    });
    const [confirm, ConfirmDialog] = useConfirm();

    const [selectedGoogleIntegration, setSelectedGoogleIntegration] =
        React.useState(null);

    const handleIntegrationCreated = () => {
        // Refresh the list after creating or updating an integration
        call();
        // Reset editing state
        setIsEditing(false);
        setIntegrationToEdit(null);
    };

    const handleEditIntegration = (integration) => {
        setIsEditing(true);
        setIntegrationToEdit(integration);
        setShowNewModal(true);
        if (integration.type === "google") {
            setSelectedGoogleIntegration(integration);
        }
    };

    const handleOpenTemplateModal = (integration) => {
        setSelectedWhatsAppIntegration(integration);
        setShowTemplateModal(true);
    };

    const handleCopyEmail = async (email, integrationId) => {
        try {
            await navigator.clipboard.writeText(email);
            setCopySuccess(integrationId);
            setTimeout(() => setCopySuccess(null), 2000); // Reset after 2 seconds
        } catch (err) {
            console.error("Failed to copy email:", err);
        }
    };

    const getConnectedIntegrations = () => {
        if (!response?.data) return [];
        return response.data.map((integration) => {
            // For browser integrations, use the custom name if available
            if (integration.type === "browser") {
                return {
                    ...integration,
                    name: integration.name || "Browser",
                    icon: "Chrome",
                    category: "Browser",
                    isCustom: true,
                };
            }
            // Find the matching integration type from available integrations
            const matchingIntegrationType = AvailableIntegrations.find(
                (ai) => ai.id === integration.type
            );
            // Community integrations have no INTEGRATION_TYPES entry — pull the
            // logo from the linked CommunityIntegration blueprint, which the
            // backend joins into `logoUrl` (see getIntegrationsList.js).
            const isCommunity = integration.platform === "community";
            // For other integrations, use the predefined data
            return {
                ...integration,
                icon: matchingIntegrationType?.icon || (isCommunity ? "Globe" : "Puzzle"),
                imageIcon:
                    integration.logoUrl ||
                    matchingIntegrationType?.imageIcon,
                name:
                    integration.name ||
                    integration.communityName ||
                    matchingIntegrationType?.name ||
                    integration.name,
                category:
                    matchingIntegrationType?.category ||
                    (isCommunity ? "Community" : "Uncategorized"),
                viewOnly: matchingIntegrationType?.viewOnly || false,
            };
        });
    };

    const fetchIntegrations = async () => {
        await call({
            limit: 10000,
            // Filter out disconnected/soft-deleted rows. The backend defaults to
            // returning every row regardless of isActive (preserving CLI/AI
            // callers that may want the full set), so the UI must opt in.
            isActive: "true",
        });
    };

    const handleDeleteIntegration = async (integrationId) => {
        try {
            // Show confirmation dialog
            await confirm({
                title: t("integrations.actions.delete"),
                description: t("integrations.actions.deleteConfirm"),
                confirmText: t("integrations.actions.deleteButton"),
                cancelText: t("integrations.actions.cancel"),
                isDestructive: true,
            });

            // If user confirms, proceed with deletion
            await deleteCall({
                integrationId,
            });
            toast.success("Integration deleted successfully");
            fetchIntegrations();
        } catch (error) {
            // If user cancels the confirmation dialog, error will be undefined
            if (error) {
                console.error("Error deleting integration:", error);
                toast.error("Error deleting integration");
            }
        }
    };

    React.useEffect(() => {
        // Refetch when entering the My Connections tab so a disconnect performed
        // in the Community tab clears the stale row from this list.
        if (activeTab === "connections") fetchIntegrations();
    }, [activeTab]);

    // Auto-open modal if navigated with state to preselect an integration type (e.g., Shopify)
    React.useEffect(() => {
        const state = location?.state;
        if (state?.openNewIntegrationModal) {
            const preselectId = state?.preselectIntegrationTypeId;
            if (preselectId) {
                const typeToSelect = INTEGRATION_TYPES.find(
                    (t) => t.id === preselectId
                ) || { id: preselectId };
                setSelectedIntegrationType(typeToSelect);
            }
            setShowNewModal(true);
        }
        // Note: we intentionally do not clear history state here to avoid side effects.
        // Consumers should navigate without state when they don't want auto-open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.state]);

    const handleOpenIntegrationByType = (typeId) => {
        const integrationType = INTEGRATION_TYPES.find((t) => t.id === typeId);
        if (!integrationType) return;
        if (typeId === "whatsapp") {
            setShowWhatsAppModal(true);
        } else if (typeId === "facebook") {
            setShowFacebookAdsModal(true);
        } else {
            setSelectedIntegrationType(integrationType);
            setShowNewModal(true);
        }
    };

    // Use pieces data

    // Update getAvailableIntegrations to use pieces data
    const getAvailableIntegrations = () => {
        // Combine static INTEGRATION_TYPES with dynamic pieces data
        const allIntegrations = [...INTEGRATION_TYPES];

        return allIntegrations.filter(
            (integration) =>
                integration.id !== "browser" &&
                !integration.viewOnly &&
                (isSuperAdmin || !integration.inDevelopment)
        );
    };

    // Helper function to check if a name is unique for a given integration type
    const isNameUniqueForType = (name, type, excludeId = null) => {
        const connectedIntegrations = response?.data || [];
        return !connectedIntegrations.some(
            (integration) =>
                integration.type === type &&
                integration.name === name &&
                integration.id !== excludeId
        );
    };

    // Get connected integrations
    const connectedIntegrations = getConnectedIntegrations().filter(
        (integration) => {
            const searchText = filterText.toLowerCase();
            return (
                integration.name.toLowerCase().includes(searchText) ||
                (integration.category || "").toLowerCase().includes(searchText)
            );
        }
    );

    // Get available integration types (for adding new ones)
    const availableIntegrationTypes = getAvailableIntegrations().filter(
        (integration) => {
            const searchText = filterText.toLowerCase();
            return (
                integration.name.toLowerCase().includes(searchText) ||
                (integration.category || "").toLowerCase().includes(searchText)
            );
        }
    );

    const hasNoIntegrations =
        !isLoading &&
        connectedIntegrations.length === 0 &&
        availableIntegrationTypes.length === 0;

    if (error) {
        return (
            <MainLayout>
                <div className="text-center text-red-500">
                    Error loading integrations: {error.message}
                </div>
            </MainLayout>
        );
    }

    if (browserIntegration) {
        return (
            <MainLayout>
                <div className="flex flex-col h-full">
                    {/* Navigation header */}
                    <div className="flex items-center justify-between bg-white p-4 border-b">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setBrowserIntegration(null)}
                                className="p-2 hover:bg-gray-100 rounded-full"
                                title="Back to connections"
                            >
                                <Icon name="ArrowLeft" className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-2">
                                <Icon name="Chrome" className="w-6 h-6" />
                                <span className="font-medium">
                                    {browserIntegration.name ||
                                        "Browser Session"}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2"></div>
                    </div>

                    {/* Browser iframe container */}
                    <div className="flex-1 bg-gray-100">
                        <iframe
                            src={`${browserIntegration.debugUrl}?theme=light&interactive=true&showControls=true`}
                            className="w-full h-full border-0"
                            title="Browser Integration Session"
                        />
                    </div>
                </div>
            </MainLayout>
        );
    }
    return (
        <MainLayout>
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                    <h1 className="page-title">
                        {t("integrations.connections")}
                    </h1>
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        {activeTab === "connections" && (
                            <input
                                type="text"
                                placeholder={t(
                                    "integrations.filterConnections"
                                )}
                                className="px-4 py-2 border rounded-md w-full sm:w-auto"
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        )}
                    </div>
                </div>

                {/* Community integration banner */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 text-white">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <Icon
                                name="Sparkles"
                                className="w-5 h-5 shrink-0"
                            />
                            <div>
                                <span className="font-medium text-sm">
                                    {t("communityIntegrations.banner.title")}
                                </span>
                                <p className="text-blue-200 text-xs mt-0.5">
                                    {t("communityIntegrations.banner.subtitle")}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCreateCommunityModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-white text-blue-700 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors shrink-0"
                        >
                            <Icon name="Plus" className="w-4 h-4" />
                            {t("communityIntegrations.createButton")}
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-4 border-b">
                    <button
                        onClick={() => setActiveTab("connections")}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === "connections"
                                ? "border-blue-600 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        <span className="flex items-center gap-2">
                            <Icon name="Puzzle" className="w-4 h-4" />
                            My Connections
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab("community")}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === "community"
                                ? "border-blue-600 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        <span className="flex items-center gap-2">
                            <Icon name="Globe" className="w-4 h-4" />
                            Community
                            <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                Early Beta
                            </span>
                        </span>
                    </button>
                </div>

                {activeTab === "community" ? (
                    <CommunityIntegrations autoOpenCreate={autoOpenCreate} />
                ) : (
                    <>
                        <IntegrationAlerts />
                        {blueprintMissing.length > 0 && (
                            <div className="bg-violet-50 border-2 border-violet-300 rounded-lg p-5 mb-4 shadow-md animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 bg-violet-100 rounded-full p-2">
                                        <Icon
                                            name="Puzzle"
                                            className="w-6 h-6 text-violet-600"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-violet-900 text-lg mb-1">
                                            {t(
                                                "integrations.blueprintSetup.title",
                                                {
                                                    name: blueprintName,
                                                }
                                            )}
                                        </h3>
                                        <p className="text-violet-700 mb-4">
                                            {t(
                                                "integrations.blueprintSetup.description"
                                            )}
                                        </p>
                                        <div className="flex flex-wrap gap-3">
                                            {blueprintMissing.map((typeId) => {
                                                const type =
                                                    INTEGRATION_TYPES.find(
                                                        (t) => t.id === typeId
                                                    );
                                                return (
                                                    <button
                                                        key={typeId}
                                                        onClick={() =>
                                                            handleOpenIntegrationByType(
                                                                typeId
                                                            )
                                                        }
                                                        className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white font-medium rounded-md hover:bg-violet-700 shadow-sm transition-colors"
                                                    >
                                                        <Icon
                                                            name="Plus"
                                                            className="w-4 h-4"
                                                        />
                                                        {type?.name || typeId}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <Outlet />

                        {/* Starter Plan / Trial Message */}
                        {showStarterMessage && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-4">
                                <div className="flex items-start gap-3">
                                    <Icon
                                        name="AlertCircle"
                                        className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5"
                                    />
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-yellow-900 mb-2">
                                            {t(
                                                "integrations.notAvailable.title"
                                            )}
                                        </h3>
                                        <p className="text-yellow-800 mb-4">
                                            {t(
                                                "integrations.notAvailable.message"
                                            )}
                                        </p>
                                        <InternalLink
                                            to="/billing"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                                        >
                                            <span>
                                                {t(
                                                    "integrations.notAvailable.upgradePlan"
                                                )}
                                            </span>
                                            <Icon
                                                name="ArrowRight"
                                                className="w-4 h-4"
                                            />
                                        </InternalLink>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Empty state */}
                        {hasNoIntegrations ? (
                            <div className="bg-white rounded-lg shadow p-4 sm:p-8">
                                <div className="text-center">
                                    <div className="inline-block p-4 bg-gray-100 rounded-full mb-4">
                                        <Icon
                                            name="Puzzle"
                                            className="w-12 h-12 text-gray-500"
                                        />
                                    </div>
                                    <h3 className="text-xl font-medium mb-2">
                                        {t("integrations.getStarted.title")}
                                    </h3>
                                    <p className="text-gray-600 mb-6">
                                        {t(
                                            "integrations.getStarted.description"
                                        )}
                                    </p>
                                    <button
                                        onClick={() => {
                                            if (integrationsEnabled) {
                                                setShowNewModal(true);
                                            }
                                        }}
                                        className={`px-6 py-3 text-white rounded-md flex items-center gap-2 mx-auto ${
                                            integrationsEnabled
                                                ? "bg-blue-600 hover:bg-blue-700"
                                                : "bg-gray-400 cursor-not-allowed"
                                        }`}
                                        disabled={!integrationsEnabled}
                                    >
                                        <Icon name="Plus" className="w-5 h-5" />
                                        <span className="hidden sm:inline">
                                            {t(
                                                "integrations.getStarted.createFirst"
                                            )}
                                        </span>
                                        <span className="sm:hidden">
                                            {t(
                                                "integrations.getStarted.create"
                                            )}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* <div className="bg-pink-50 p-4 rounded-md flex justify-between items-center mb-4">
                            <div>
                                <span className="font-medium">
                                    Don't see your favorite application here?{" "}
                                </span>
                                <span>Build your own custom integration</span>
                            </div>
                            <button
                                onClick={() => setShowNewModal(true)}
                                className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                            >
                                Create Custom Connection
                            </button>
                        </div> */}

                                {/* Desktop Table View */}
                                <div className="hidden md:block bg-white rounded-lg shadow">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left px-6 py-3">
                                                    {t(
                                                        "integrations.table.connection"
                                                    )}
                                                </th>
                                                <th className="text-left px-6 py-3">
                                                    {t(
                                                        "integrations.table.status"
                                                    )}
                                                </th>
                                                <th className="text-left px-6 py-3">
                                                    {t(
                                                        "integrations.table.category"
                                                    )}
                                                </th>
                                                <th className="text-left px-6 py-3">
                                                    {t(
                                                        "integrations.table.action"
                                                    )}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isLoading
                                                ? // Loading state
                                                  Array.from({ length: 4 }).map(
                                                      (_, index) => (
                                                          <tr
                                                              key={`loading-${index}`}
                                                              className="border-b"
                                                          >
                                                              <td className="px-6 py-4">
                                                                  <div className="animate-pulse flex items-center gap-3">
                                                                      <div className="w-8 h-8 bg-gray-200 rounded"></div>
                                                                      <div className="w-24 h-4 bg-gray-200 rounded"></div>
                                                                  </div>
                                                              </td>
                                                              <td className="px-6 py-4">
                                                                  <div className="animate-pulse w-16 h-4 bg-gray-200 rounded"></div>
                                                              </td>
                                                              <td className="px-6 py-4">
                                                                  <div className="animate-pulse w-24 h-4 bg-gray-200 rounded"></div>
                                                              </td>
                                                              <td className="px-6 py-4">
                                                                  <div className="animate-pulse w-8 h-8 bg-gray-200 rounded"></div>
                                                              </td>
                                                          </tr>
                                                      )
                                                  )
                                                : [
                                                      // Connected integrations first
                                                      ...connectedIntegrations.map(
                                                          (integration) => (
                                                              <React.Fragment
                                                                  key={`connected-${integration.id}`}
                                                              >
                                                                  <tr className="border-b hover:bg-gray-50">
                                                                      <td className="px-6 py-4">
                                                                          <div className="flex items-center gap-3">
                                                                              {integration?.imageIcon ? (
                                                                                  <img
                                                                                      src={
                                                                                          integration?.imageIcon
                                                                                      }
                                                                                      alt={`${integration.name} icon`}
                                                                                      className="w-10 h-10 object-contain"
                                                                                  />
                                                                              ) : (
                                                                                  <Icon
                                                                                      name={
                                                                                          integration.icon
                                                                                      }
                                                                                      className="w-8 h-8"
                                                                                  />
                                                                              )}
                                                                              <div>
                                                                                  <span className="font-medium">
                                                                                      {
                                                                                          integration.name
                                                                                      }
                                                                                  </span>
                                                                                  {integration.isCustom && (
                                                                                      <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                                                                          {t(
                                                                                              "integrations.status.custom"
                                                                                          )}
                                                                                      </span>
                                                                                  )}
                                                                                  {integration.subIcons && (
                                                                                      <div className="flex items-center gap-1 mt-1">
                                                                                          {integration.subIcons.map(
                                                                                              (
                                                                                                  sub
                                                                                              ) => (
                                                                                                  <img
                                                                                                      key={
                                                                                                          sub.label
                                                                                                      }
                                                                                                      src={
                                                                                                          sub.src
                                                                                                      }
                                                                                                      alt={
                                                                                                          sub.label
                                                                                                      }
                                                                                                      title={
                                                                                                          sub.label
                                                                                                      }
                                                                                                      className="w-5 h-5 object-contain"
                                                                                                  />
                                                                                              )
                                                                                          )}
                                                                                      </div>
                                                                                  )}
                                                                              </div>
                                                                          </div>
                                                                      </td>
                                                                      <td className="px-6 py-4">
                                                                          <div className="flex items-center gap-2">
                                                                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                                              <span>
                                                                                  {t(
                                                                                      "integrations.status.connected"
                                                                                  )}
                                                                              </span>
                                                                          </div>
                                                                      </td>
                                                                      <td className="px-6 py-4">
                                                                          {integration.category ||
                                                                              "Uncategorized"}
                                                                      </td>
                                                                      <td className="px-6 py-4">
                                                                          <div className="flex items-center gap-2">
                                                                              {integration.platform ===
                                                                                  "whatsapp" && (
                                                                                  <Tooltip>
                                                                                      <TooltipTrigger
                                                                                          asChild
                                                                                      >
                                                                                          <button
                                                                                              className="p-2 hover:bg-gray-100 rounded-full text-green-600"
                                                                                              onClick={() =>
                                                                                                  handleOpenTemplateModal(
                                                                                                      integration
                                                                                                  )
                                                                                              }
                                                                                          >
                                                                                              <Icon
                                                                                                  name="MessageSquare"
                                                                                                  className="w-5 h-5"
                                                                                              />
                                                                                          </button>
                                                                                      </TooltipTrigger>
                                                                                      <TooltipContent>
                                                                                          <p>
                                                                                              {t(
                                                                                                  "integrations.actions.manageTemplates"
                                                                                              )}
                                                                                          </p>
                                                                                      </TooltipContent>
                                                                                  </Tooltip>
                                                                              )}
                                                                              {!integration.viewOnly && integration.platform !== "community" && (
                                                                                  <button
                                                                                      className="p-2 hover:bg-gray-100 rounded-full text-blue-600"
                                                                                      onClick={() =>
                                                                                          handleEditIntegration(
                                                                                              integration
                                                                                          )
                                                                                      }
                                                                                  >
                                                                                      <Icon
                                                                                          name="Pencil"
                                                                                          className="w-5 h-5"
                                                                                      />
                                                                                  </button>
                                                                              )}
                                                                              <button
                                                                                  className="p-2 hover:bg-gray-100 rounded-full text-red-600"
                                                                                  onClick={() =>
                                                                                      handleDeleteIntegration(
                                                                                          integration.id
                                                                                      )
                                                                                  }
                                                                              >
                                                                                  <Icon
                                                                                      name="Trash"
                                                                                      className="w-5 h-5"
                                                                                  />
                                                                              </button>
                                                                          </div>
                                                                      </td>
                                                                  </tr>
                                                                  {integration.type ===
                                                                      "google" &&
                                                                      integration.isActive && (
                                                                          <tr className="border-b bg-yellow-50">
                                                                              <td
                                                                                  colSpan="4"
                                                                                  className="px-6 py-4"
                                                                              >
                                                                                  <Message
                                                                                      warning
                                                                                  >
                                                                                      <div className="flex flex-col gap-4">
                                                                                          <p>
                                                                                              You
                                                                                              need
                                                                                              to
                                                                                              specify
                                                                                              which
                                                                                              files
                                                                                              you
                                                                                              want
                                                                                              to
                                                                                              access
                                                                                              in
                                                                                              your
                                                                                              Google
                                                                                              Drive
                                                                                              integration.
                                                                                          </p>
                                                                                          <GoogleDrivePicker
                                                                                              integrationId={
                                                                                                  integration.id
                                                                                              }
                                                                                              onSelect={(
                                                                                                  files
                                                                                              ) => {
                                                                                                  // Handle the selected files here
                                                                                              }}
                                                                                              buttonText="Select Google Drive Files"
                                                                                              multiSelect={
                                                                                                  true
                                                                                              }
                                                                                          />
                                                                                      </div>
                                                                                  </Message>
                                                                              </td>
                                                                          </tr>
                                                                      )}
                                                                  {integration.type ===
                                                                      "sql" && (
                                                                      <tr className="border-b bg-blue-50">
                                                                          <td
                                                                              colSpan="4"
                                                                              className="px-6 py-4"
                                                                          >
                                                                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                                  <div className="flex items-start gap-3">
                                                                                      <Icon
                                                                                          name="Info"
                                                                                          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                                                                                      />
                                                                                      <div className="flex-1">
                                                                                          <h4 className="font-medium text-blue-900 mb-2">
                                                                                              {t(
                                                                                                  "integrations.sql.privateIP.title"
                                                                                              )}
                                                                                          </h4>
                                                                                          <p className="text-sm text-blue-800 mb-3">
                                                                                              {t(
                                                                                                  "integrations.sql.privateIP.description"
                                                                                              )}
                                                                                          </p>
                                                                                          <p className="text-sm text-blue-800">
                                                                                              {t(
                                                                                                  "integrations.sql.privateIP.emailText"
                                                                                              )}{" "}
                                                                                              <a
                                                                                                  href="mailto:support@agentui.ai?subject=Request Private IP for SQL Firewall&body=Hello, I would like to request a private IP address to add to my SQL database firewall whitelist."
                                                                                                  className="font-medium underline hover:text-blue-900"
                                                                                              >
                                                                                                  support@agentui.ai
                                                                                              </a>
                                                                                          </p>
                                                                                      </div>
                                                                                  </div>
                                                                              </div>
                                                                          </td>
                                                                      </tr>
                                                                  )}
                                                              </React.Fragment>
                                                          )
                                                      ),
                                                      // Available integration types to add
                                                      ...availableIntegrationTypes.map(
                                                          (integrationType) => (
                                                              <React.Fragment
                                                                  key={`available-${integrationType.id}`}
                                                              >
                                                                  <tr className="border-b hover:bg-gray-50 opacity-100">
                                                                      <td className="px-6 py-4">
                                                                          <div className="flex items-center gap-3">
                                                                              {integrationType?.imageIcon ? (
                                                                                  <img
                                                                                      src={
                                                                                          integrationType?.imageIcon
                                                                                      }
                                                                                      alt={`${integrationType.name} icon`}
                                                                                      className="w-10 h-10 object-contain "
                                                                                  />
                                                                              ) : (
                                                                                  <Icon
                                                                                      name={
                                                                                          integrationType.icon
                                                                                      }
                                                                                      className="w-8 h-8 text-gray-400"
                                                                                  />
                                                                              )}
                                                                              <div>
                                                                                  <span className="font-medium text-gray-600">
                                                                                      {
                                                                                          integrationType.name
                                                                                      }
                                                                                  </span>
                                                                                  <span className="ml-2 text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                                                                      {t(
                                                                                          "integrations.status.available"
                                                                                      )}
                                                                                  </span>
                                                                                  {integrationType.subIcons && (
                                                                                      <div className="flex items-center gap-1 mt-1">
                                                                                          {integrationType.subIcons.map(
                                                                                              (
                                                                                                  sub
                                                                                              ) => (
                                                                                                  <img
                                                                                                      key={
                                                                                                          sub.label
                                                                                                      }
                                                                                                      src={
                                                                                                          sub.src
                                                                                                      }
                                                                                                      alt={
                                                                                                          sub.label
                                                                                                      }
                                                                                                      title={
                                                                                                          sub.label
                                                                                                      }
                                                                                                      className="w-5 h-5 object-contain opacity-60"
                                                                                                  />
                                                                                              )
                                                                                          )}
                                                                                      </div>
                                                                                  )}
                                                                              </div>
                                                                          </div>
                                                                      </td>
                                                                      <td className="px-6 py-4">
                                                                          <div className="flex items-center gap-2">
                                                                              <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                                                                              <span className="text-gray-500">
                                                                                  {t(
                                                                                      "integrations.status.notConnected"
                                                                                  )}
                                                                              </span>
                                                                          </div>
                                                                      </td>
                                                                      <td className="px-6 py-4 text-gray-500">
                                                                          {integrationType.category ||
                                                                              "Uncategorized"}
                                                                      </td>
                                                                      <td className="px-6 py-4">
                                                                          <div className="flex items-center gap-2">
                                                                              {(() => {
                                                                                  const isTypeRestricted =
                                                                                      (integrationType.id ===
                                                                                          "sql" &&
                                                                                          !hasSqlAccess) ||
                                                                                      (integrationType.id ===
                                                                                          "facebook" &&
                                                                                          !hasFacebookAdsAccess);
                                                                                  return (
                                                                                      <>
                                                                                          <button
                                                                                              className={`px-3 py-1 text-white rounded-md text-sm ${
                                                                                                  integrationsEnabled &&
                                                                                                  !isTypeRestricted
                                                                                                      ? "bg-blue-600 hover:bg-blue-700"
                                                                                                      : "bg-gray-400 cursor-not-allowed"
                                                                                              }`}
                                                                                              onClick={() => {
                                                                                                  if (
                                                                                                      !integrationsEnabled
                                                                                                  )
                                                                                                      return;
                                                                                                  if (
                                                                                                      isTypeRestricted
                                                                                                  ) {
                                                                                                      toast.error(
                                                                                                          `${integrationType.name} is available on Innovator plan ($100/month) and above. Upgrade your plan to use this integration.`
                                                                                                      );
                                                                                                      return;
                                                                                                  }
                                                                                                  if (
                                                                                                      integrationType.id ===
                                                                                                      "whatsapp"
                                                                                                  ) {
                                                                                                      setShowWhatsAppModal(
                                                                                                          true
                                                                                                      );
                                                                                                  } else if (
                                                                                                      integrationType.id ===
                                                                                                      "facebook"
                                                                                                  ) {
                                                                                                      setShowFacebookAdsModal(
                                                                                                          true
                                                                                                      );
                                                                                                  } else {
                                                                                                      setShowNewModal(
                                                                                                          true
                                                                                                      );
                                                                                                      setSelectedIntegrationType(
                                                                                                          integrationType
                                                                                                      );
                                                                                                  }
                                                                                              }}
                                                                                              disabled={
                                                                                                  !integrationsEnabled ||
                                                                                                  isTypeRestricted
                                                                                              }
                                                                                          >
                                                                                              {t(
                                                                                                  "integrations.actions.add"
                                                                                              )}
                                                                                          </button>
                                                                                          {isTypeRestricted && (
                                                                                              <span className="text-xs text-purple-600 font-medium">
                                                                                                  Innovator+
                                                                                              </span>
                                                                                          )}
                                                                                      </>
                                                                                  );
                                                                              })()}
                                                                          </div>
                                                                      </td>
                                                                  </tr>
                                                                  {/* Show capabilities for available integrations */}
                                                                  {integrationType.capabilities && (
                                                                      <tr className="border-b bg-blue-50">
                                                                          <td
                                                                              colSpan="4"
                                                                              className="px-6 py-4"
                                                                          >
                                                                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                                  <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                                                                                      <Icon
                                                                                          name="Info"
                                                                                          className="w-4 h-4 mr-2"
                                                                                      />
                                                                                      {t(
                                                                                          "integrations.capabilities.title"
                                                                                      )}
                                                                                  </h4>
                                                                                  <ul className="space-y-2">
                                                                                      {integrationType.capabilities.map(
                                                                                          (
                                                                                              capability,
                                                                                              index
                                                                                          ) => (
                                                                                              <li
                                                                                                  key={
                                                                                                      index
                                                                                                  }
                                                                                                  className="text-sm text-blue-800 flex items-start"
                                                                                              >
                                                                                                  <Icon
                                                                                                      name="Check"
                                                                                                      className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0"
                                                                                                  />
                                                                                                  {
                                                                                                      capability
                                                                                                  }
                                                                                              </li>
                                                                                          )
                                                                                      )}
                                                                                  </ul>
                                                                              </div>
                                                                          </td>
                                                                      </tr>
                                                                  )}
                                                                  {/* Show private IP section for SQL integrations */}
                                                                  {integrationType.id ===
                                                                      "sql" && (
                                                                      <tr className="border-b bg-blue-50">
                                                                          <td
                                                                              colSpan="4"
                                                                              className="px-6 py-4"
                                                                          >
                                                                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                                  <div className="flex items-start gap-3">
                                                                                      <Icon
                                                                                          name="Info"
                                                                                          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                                                                                      />
                                                                                      <div className="flex-1">
                                                                                          <h4 className="font-medium text-blue-900 mb-2">
                                                                                              {t(
                                                                                                  "integrations.sql.privateIP.title"
                                                                                              )}
                                                                                          </h4>
                                                                                          <p className="text-sm text-blue-800 mb-3">
                                                                                              {t(
                                                                                                  "integrations.sql.privateIP.description"
                                                                                              )}
                                                                                          </p>
                                                                                          <p className="text-sm text-blue-800">
                                                                                              {t(
                                                                                                  "integrations.sql.privateIP.emailText"
                                                                                              )}{" "}
                                                                                              <a
                                                                                                  href="mailto:support@agentui.ai?subject=Request Private IP for SQL Firewall&body=Hello, I would like to request a private IP address to add to my SQL database firewall whitelist."
                                                                                                  className="font-medium underline hover:text-blue-900"
                                                                                              >
                                                                                                  support@agentui.ai
                                                                                              </a>
                                                                                          </p>
                                                                                      </div>
                                                                                  </div>
                                                                              </div>
                                                                          </td>
                                                                      </tr>
                                                                  )}
                                                                  {/* Show included WhatsApp numbers info */}
                                                                  {integrationType.id ===
                                                                      "whatsapp" && (
                                                                      <tr className="border-b bg-green-50">
                                                                          <td
                                                                              colSpan="4"
                                                                              className="px-6 py-3"
                                                                          >
                                                                              <div className="flex items-start gap-3">
                                                                                  <Icon
                                                                                      name="CheckCircle"
                                                                                      className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                                                                                  />
                                                                                  <div>
                                                                                      <h4 className="font-medium text-sm text-green-800">
                                                                                          {t(
                                                                                              "integrations.whatsappSetup.includedNumbers.title"
                                                                                          )}
                                                                                      </h4>
                                                                                      <p className="text-xs text-green-700 mt-1">
                                                                                          {t(
                                                                                              "integrations.whatsappSetup.includedNumbers.description"
                                                                                          )}
                                                                                      </p>
                                                                                  </div>
                                                                              </div>
                                                                          </td>
                                                                      </tr>
                                                                  )}
                                                              </React.Fragment>
                                                          )
                                                      ),
                                                  ]}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Card View */}
                                <div className="md:hidden space-y-4">
                                    {isLoading
                                        ? // Loading state for mobile
                                          Array.from({ length: 4 }).map(
                                              (_, index) => (
                                                  <div
                                                      key={`mobile-loading-${index}`}
                                                      className="bg-white rounded-lg shadow p-4"
                                                  >
                                                      <div className="animate-pulse">
                                                          <div className="flex items-center gap-3 mb-3">
                                                              <div className="w-12 h-12 bg-gray-200 rounded"></div>
                                                              <div className="flex-1">
                                                                  <div className="w-32 h-4 bg-gray-200 rounded mb-2"></div>
                                                                  <div className="w-24 h-3 bg-gray-200 rounded"></div>
                                                              </div>
                                                          </div>
                                                          <div className="w-20 h-6 bg-gray-200 rounded"></div>
                                                      </div>
                                                  </div>
                                              )
                                          )
                                        : [
                                              // Connected integrations first
                                              ...connectedIntegrations.map(
                                                  (integration) => (
                                                      <React.Fragment
                                                          key={`mobile-connected-${integration.id}`}
                                                      >
                                                          <div className="bg-white rounded-lg shadow p-4">
                                                              <div className="flex items-start justify-between mb-3">
                                                                  <div className="flex items-center gap-3">
                                                                      {integration?.imageIcon ? (
                                                                          <img
                                                                              src={
                                                                                  integration?.imageIcon
                                                                              }
                                                                              alt={`${integration.name} icon`}
                                                                              className="w-12 h-12 object-contain"
                                                                          />
                                                                      ) : (
                                                                          <Icon
                                                                              name={
                                                                                  integration.icon
                                                                              }
                                                                              className="w-12 h-12"
                                                                          />
                                                                      )}
                                                                      <div>
                                                                          <h3 className="font-medium text-lg">
                                                                              {
                                                                                  integration.name
                                                                              }
                                                                          </h3>
                                                                          <p className="text-sm text-gray-600">
                                                                              {integration.category ||
                                                                                  "Uncategorized"}
                                                                          </p>
                                                                          {integration.subIcons && (
                                                                              <div className="flex items-center gap-1 mt-1">
                                                                                  {integration.subIcons.map(
                                                                                      (
                                                                                          sub
                                                                                      ) => (
                                                                                          <img
                                                                                              key={
                                                                                                  sub.label
                                                                                              }
                                                                                              src={
                                                                                                  sub.src
                                                                                              }
                                                                                              alt={
                                                                                                  sub.label
                                                                                              }
                                                                                              title={
                                                                                                  sub.label
                                                                                              }
                                                                                              className="w-5 h-5 object-contain"
                                                                                          />
                                                                                      )
                                                                                  )}
                                                                              </div>
                                                                          )}
                                                                      </div>
                                                                  </div>
                                                                  <div className="flex items-center gap-2">
                                                                      {integration.platform ===
                                                                          "whatsapp" && (
                                                                          <TooltipProvider>
                                                                              <Tooltip>
                                                                                  <TooltipTrigger
                                                                                      asChild
                                                                                  >
                                                                                      <button
                                                                                          className="p-2 hover:bg-gray-100 rounded-full text-green-600"
                                                                                          onClick={() =>
                                                                                              handleOpenTemplateModal(
                                                                                                  integration
                                                                                              )
                                                                                          }
                                                                                      >
                                                                                          <Icon
                                                                                              name="MessageSquare"
                                                                                              className="w-5 h-5"
                                                                                          />
                                                                                      </button>
                                                                                  </TooltipTrigger>
                                                                                  <TooltipContent>
                                                                                      <p>
                                                                                          {t(
                                                                                              "integrations.actions.manageTemplates"
                                                                                          )}
                                                                                      </p>
                                                                                  </TooltipContent>
                                                                              </Tooltip>
                                                                          </TooltipProvider>
                                                                      )}
                                                                      {!integration.viewOnly && integration.platform !== "community" && (
                                                                          <button
                                                                              className="p-2 hover:bg-gray-100 rounded-full text-blue-600"
                                                                              onClick={() =>
                                                                                  handleEditIntegration(
                                                                                      integration
                                                                                  )
                                                                              }
                                                                          >
                                                                              <Icon
                                                                                  name="Pencil"
                                                                                  className="w-5 h-5"
                                                                              />
                                                                          </button>
                                                                      )}
                                                                      <button
                                                                          className="p-2 hover:bg-gray-100 rounded-full text-red-600"
                                                                          onClick={() =>
                                                                              handleDeleteIntegration(
                                                                                  integration.id
                                                                              )
                                                                          }
                                                                      >
                                                                          <Icon
                                                                              name="Trash"
                                                                              className="w-5 h-5"
                                                                          />
                                                                      </button>
                                                                  </div>
                                                              </div>
                                                              <div className="flex items-center justify-between">
                                                                  <div className="flex items-center gap-2">
                                                                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                                      <span className="text-sm">
                                                                          {t(
                                                                              "integrations.status.connected"
                                                                          )}
                                                                      </span>
                                                                  </div>
                                                                  {integration.isCustom && (
                                                                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                                                          {t(
                                                                              "integrations.status.custom"
                                                                          )}
                                                                      </span>
                                                                  )}
                                                              </div>
                                                          </div>
                                                          {integration.type ===
                                                              "google" &&
                                                              integration.isActive && (
                                                                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                                                                      <Message
                                                                          warning
                                                                      >
                                                                          <div className="flex flex-col gap-4">
                                                                              <p>
                                                                                  You
                                                                                  need
                                                                                  to
                                                                                  specify
                                                                                  which
                                                                                  files
                                                                                  you
                                                                                  want
                                                                                  to
                                                                                  access
                                                                                  in
                                                                                  your
                                                                                  Google
                                                                                  Drive
                                                                                  integration.
                                                                              </p>
                                                                              <GoogleDrivePicker
                                                                                  integrationId={
                                                                                      integration.id
                                                                                  }
                                                                                  onSelect={(
                                                                                      files
                                                                                  ) => {
                                                                                      // Handle the selected files here
                                                                                  }}
                                                                                  buttonText="Select Google Drive Files"
                                                                                  multiSelect={
                                                                                      true
                                                                                  }
                                                                              />
                                                                          </div>
                                                                      </Message>
                                                                  </div>
                                                              )}
                                                          {integration.type ===
                                                              "sql" && (
                                                              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                  <div className="flex items-start gap-3">
                                                                      <Icon
                                                                          name="Info"
                                                                          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                                                                      />
                                                                      <div className="flex-1">
                                                                          <h4 className="font-medium text-blue-900 mb-2">
                                                                              {t(
                                                                                  "integrations.sql.privateIP.title"
                                                                              )}
                                                                          </h4>
                                                                          <p className="text-sm text-blue-800 mb-3">
                                                                              {t(
                                                                                  "integrations.sql.privateIP.description"
                                                                              )}
                                                                          </p>
                                                                          <p className="text-sm text-blue-800">
                                                                              {t(
                                                                                  "integrations.sql.privateIP.emailText"
                                                                              )}{" "}
                                                                              <a
                                                                                  href="mailto:support@agentui.ai?subject=Request Private IP for SQL Firewall&body=Hello, I would like to request a private IP address to add to my SQL database firewall whitelist."
                                                                                  className="font-medium underline hover:text-blue-900"
                                                                              >
                                                                                  support@agentui.ai
                                                                              </a>
                                                                          </p>
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                          )}
                                                      </React.Fragment>
                                                  )
                                              ),
                                              // Available integration types to add
                                              ...availableIntegrationTypes.map(
                                                  (integrationType) => (
                                                      <div
                                                          key={`mobile-available-${integrationType.id}`}
                                                          className="bg-white rounded-lg shadow p-4"
                                                      >
                                                          <div className="flex items-start justify-between mb-3">
                                                              <div className="flex items-center gap-3">
                                                                  {integrationType?.imageIcon ? (
                                                                      <img
                                                                          src={
                                                                              integrationType?.imageIcon
                                                                          }
                                                                          alt={`${integrationType.name} icon`}
                                                                          className="w-12 h-12 object-contain opacity-60"
                                                                      />
                                                                  ) : (
                                                                      <Icon
                                                                          name={
                                                                              integrationType.icon
                                                                          }
                                                                          className="w-12 h-12 text-gray-400"
                                                                      />
                                                                  )}
                                                                  <div>
                                                                      <h3 className="font-medium text-lg text-gray-600">
                                                                          {
                                                                              integrationType.name
                                                                          }
                                                                      </h3>
                                                                      <p className="text-sm text-gray-500">
                                                                          {integrationType.category ||
                                                                              "Uncategorized"}
                                                                      </p>
                                                                      {integrationType.subIcons && (
                                                                          <div className="flex items-center gap-1 mt-1">
                                                                              {integrationType.subIcons.map(
                                                                                  (
                                                                                      sub
                                                                                  ) => (
                                                                                      <img
                                                                                          key={
                                                                                              sub.label
                                                                                          }
                                                                                          src={
                                                                                              sub.src
                                                                                          }
                                                                                          alt={
                                                                                              sub.label
                                                                                          }
                                                                                          title={
                                                                                              sub.label
                                                                                          }
                                                                                          className="w-5 h-5 object-contain opacity-60"
                                                                                      />
                                                                                  )
                                                                              )}
                                                                          </div>
                                                                      )}
                                                                  </div>
                                                              </div>
                                                              {(() => {
                                                                  const isTypeRestricted =
                                                                      (integrationType.id ===
                                                                          "sql" &&
                                                                          !hasSqlAccess) ||
                                                                      (integrationType.id ===
                                                                          "facebook" &&
                                                                          !hasFacebookAdsAccess);
                                                                  return (
                                                                      <>
                                                                          <button
                                                                              className={`px-4 py-2 text-white rounded-md text-sm ${
                                                                                  integrationsEnabled &&
                                                                                  !isTypeRestricted
                                                                                      ? "bg-blue-600 hover:bg-blue-700"
                                                                                      : "bg-gray-400 cursor-not-allowed"
                                                                              }`}
                                                                              onClick={() => {
                                                                                  if (
                                                                                      !integrationsEnabled
                                                                                  )
                                                                                      return;
                                                                                  if (
                                                                                      isTypeRestricted
                                                                                  ) {
                                                                                      toast.error(
                                                                                          `${integrationType.name} is available on Innovator plan ($100/month) and above. Upgrade your plan to use this integration.`
                                                                                      );
                                                                                      return;
                                                                                  }
                                                                                  if (
                                                                                      integrationType.id ===
                                                                                      "whatsapp"
                                                                                  ) {
                                                                                      setShowWhatsAppModal(
                                                                                          true
                                                                                      );
                                                                                  } else if (
                                                                                      integrationType.id ===
                                                                                      "facebook"
                                                                                  ) {
                                                                                      setShowFacebookAdsModal(
                                                                                          true
                                                                                      );
                                                                                  } else {
                                                                                      setShowNewModal(
                                                                                          true
                                                                                      );
                                                                                      setSelectedIntegrationType(
                                                                                          integrationType
                                                                                      );
                                                                                  }
                                                                              }}
                                                                              disabled={
                                                                                  !integrationsEnabled ||
                                                                                  isTypeRestricted
                                                                              }
                                                                          >
                                                                              {t(
                                                                                  "integrations.actions.add"
                                                                              )}
                                                                          </button>
                                                                          {isTypeRestricted && (
                                                                              <span className="text-xs text-purple-600 font-medium ml-2">
                                                                                  Innovator+
                                                                              </span>
                                                                          )}
                                                                      </>
                                                                  );
                                                              })()}
                                                          </div>
                                                          <div className="flex items-center gap-2">
                                                              <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                                                              <span className="text-sm text-gray-500">
                                                                  {t(
                                                                      "integrations.status.notConnected"
                                                                  )}
                                                              </span>
                                                              <span className="ml-2 text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                                                  {t(
                                                                      "integrations.status.available"
                                                                  )}
                                                              </span>
                                                          </div>
                                                          {/* Show capabilities for available integrations */}
                                                          {integrationType.capabilities && (
                                                              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                  <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                                                                      <Icon
                                                                          name="Info"
                                                                          className="w-4 h-4 mr-2"
                                                                      />
                                                                      {t(
                                                                          "integrations.capabilities.title"
                                                                      )}
                                                                  </h4>
                                                                  <ul className="space-y-2">
                                                                      {integrationType.capabilities.map(
                                                                          (
                                                                              capability,
                                                                              index
                                                                          ) => (
                                                                              <li
                                                                                  key={
                                                                                      index
                                                                                  }
                                                                                  className="text-sm text-blue-800 flex items-start"
                                                                              >
                                                                                  <Icon
                                                                                      name="Check"
                                                                                      className="w-3 h-3 mr-2 mt-0.5 flex-shrink-0"
                                                                                  />
                                                                                  {
                                                                                      capability
                                                                                  }
                                                                              </li>
                                                                          )
                                                                      )}
                                                                  </ul>
                                                              </div>
                                                          )}
                                                          {/* Show private IP section for SQL integrations */}
                                                          {integrationType.id ===
                                                              "sql" && (
                                                              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                  <div className="flex items-start gap-3">
                                                                      <Icon
                                                                          name="Info"
                                                                          className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                                                                      />
                                                                      <div className="flex-1">
                                                                          <h4 className="font-medium text-blue-900 mb-2">
                                                                              {t(
                                                                                  "integrations.sql.privateIP.title"
                                                                              )}
                                                                          </h4>
                                                                          <p className="text-sm text-blue-800 mb-3">
                                                                              {t(
                                                                                  "integrations.sql.privateIP.description"
                                                                              )}
                                                                          </p>
                                                                          <p className="text-sm text-blue-800">
                                                                              {t(
                                                                                  "integrations.sql.privateIP.emailText"
                                                                              )}{" "}
                                                                              <a
                                                                                  href="mailto:support@agentui.ai?subject=Request Private IP for SQL Firewall&body=Hello, I would like to request a private IP address to add to my SQL database firewall whitelist."
                                                                                  className="font-medium underline hover:text-blue-900"
                                                                              >
                                                                                  support@agentui.ai
                                                                              </a>
                                                                          </p>
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                          )}
                                                          {/* Show included WhatsApp numbers info */}
                                                          {integrationType.id ===
                                                              "whatsapp" && (
                                                              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                                                                  <div className="flex items-start gap-3">
                                                                      <Icon
                                                                          name="CheckCircle"
                                                                          className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                                                                      />
                                                                      <div>
                                                                          <h4 className="font-medium text-sm text-green-800">
                                                                              {t(
                                                                                  "integrations.whatsappSetup.includedNumbers.title"
                                                                              )}
                                                                          </h4>
                                                                          <p className="text-xs text-green-700 mt-1">
                                                                              {t(
                                                                                  "integrations.whatsappSetup.includedNumbers.description"
                                                                              )}
                                                                          </p>
                                                                      </div>
                                                                  </div>
                                                              </div>
                                                          )}
                                                      </div>
                                                  )
                                              ),
                                          ]}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* New/Edit Integration Modal */}
            {showNewModal && (
                <NewIntegrationModal
                    onClose={() => {
                        setShowNewModal(false);
                        setSelectedIntegrationType(null);
                        setIsEditing(false);
                        setIntegrationToEdit(null);
                    }}
                    INTEGRATION_TYPES={INTEGRATION_TYPES}
                    onSuccess={handleIntegrationCreated}
                    setBrowserIntegration={setBrowserIntegration}
                    initialIntegrationType={selectedIntegrationType}
                    isEditing={isEditing}
                    integrationToEdit={integrationToEdit}
                    isNameUniqueForType={isNameUniqueForType}
                />
            )}

            {/* WhatsApp Embedded Modal */}
            {showWhatsAppModal && (
                <WhatsAppEmbeddedModal
                    onClose={() => setShowWhatsAppModal(false)}
                    onSuccess={handleIntegrationCreated}
                />
            )}

            {/* Facebook Ads Embedded Modal */}
            {showFacebookAdsModal && (
                <FacebookAdsEmbeddedModal
                    onClose={() => setShowFacebookAdsModal(false)}
                    onSuccess={handleIntegrationCreated}
                />
            )}

            {/* WhatsApp Template Modal */}
            {showTemplateModal && selectedWhatsAppIntegration && (
                <WhatsAppTemplateModal
                    integration={selectedWhatsAppIntegration}
                    onClose={() => {
                        setShowTemplateModal(false);
                        setSelectedWhatsAppIntegration(null);
                    }}
                    onSuccess={() => {
                        // Refresh integration list if needed
                        fetchIntegrations();
                    }}
                />
            )}

            {/* Create Community Integration Modal */}
            {showCreateCommunityModal && (
                <CreateCommunityIntegrationModal
                    onClose={() => setShowCreateCommunityModal(false)}
                    onStart={(visibility) => {
                        setShowCreateCommunityModal(false);
                        nav("/integrate/build", { state: { visibility } });
                    }}
                />
            )}

            {/* Add Confirm Dialog */}
            <ConfirmDialog />
        </MainLayout>
    );
}
